/* 7LAND Ledger — storefront behaviour. No dependencies. */
(() => {
  const theme = window.theme || {};
  const cartSection = 'cart-drawer-content';

  const fetchJSON = (url, body) =>
    fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    }).then(async (r) => {
      const data = await r.json();
      if (!r.ok) throw new Error(data.description || data.message || theme.strings?.error);
      return data;
    });

  const updateCountBadges = (count) => {
    document.querySelectorAll('[data-cart-count-badge]').forEach((el) => {
      el.textContent = count;
      el.hidden = count === 0;
    });
  };

  const renderCart = (sections) => {
    const html = sections && sections[cartSection];
    if (!html) return;
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const fresh = doc.getElementById(`shopify-section-${cartSection}`);
    const current = document.getElementById(`shopify-section-${cartSection}`);
    if (fresh && current) current.replaceWith(fresh);
    const body = document.querySelector('#CartDrawer .drawer__body');
    if (body) updateCountBadges(Number(body.dataset.cartCount || 0));
  };

  /* ---------- cart drawer ---------- */
  class CartDrawer extends HTMLElement {
    connectedCallback() {
      this.addEventListener('click', (e) => {
        if (e.target.closest('[data-close]')) this.close();
        const remove = e.target.closest('[data-cart-remove]');
        if (remove) this.change(remove.dataset.cartRemove, 0);
      });
      this.addEventListener('keydown', (e) => e.key === 'Escape' && this.close());
      document.querySelectorAll('[data-open-cart]').forEach((btn) =>
        btn.addEventListener('click', (e) => {
          if (theme.cartType === 'page') return;
          e.preventDefault();
          this.open();
        })
      );
    }
    open() {
      this.lastFocus = document.activeElement;
      this.classList.add('is-open');
      this.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';
      this.querySelector('.drawer__panel')?.focus();
    }
    close() {
      this.classList.remove('is-open');
      this.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';
      this.lastFocus?.focus?.();
    }
    change(key, quantity) {
      this.classList.add('is-busy');
      return fetchJSON(`${theme.routes.cartChange}.js`, { id: key, quantity, sections: [cartSection] })
        .then((cart) => renderCart(cart.sections))
        .catch((err) => alertInline(this, err.message))
        .finally(() => this.classList.remove('is-busy'));
    }
  }
  customElements.define('cart-drawer', CartDrawer);

  const alertInline = (root, message) => {
    const box = root.querySelector('[data-form-error]');
    if (box) {
      box.textContent = message;
      box.hidden = false;
    } else {
      console.warn(message);
    }
  };

  /* ---------- quantity input (drawer, cart page, product page) ---------- */
  class QuantityInput extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('input');
      this.addEventListener('click', (e) => {
        const btn = e.target.closest('button');
        if (!btn) return;
        const step = btn.name === 'plus' ? 1 : -1;
        const min = Number(this.input.min || 0);
        this.input.value = Math.max(min, Number(this.input.value || 0) + step);
        this.input.dispatchEvent(new Event('change', { bubbles: true }));
      });
      this.input.addEventListener('change', () => {
        const key = this.dataset.lineKey;
        if (!key) return;
        const drawer = document.querySelector('cart-drawer');
        if (drawer && this.closest('cart-drawer')) {
          drawer.change(key, Number(this.input.value));
        } else {
          fetchJSON(`${theme.routes.cartChange}.js`, { id: key, quantity: Number(this.input.value) }).then(() =>
            window.location.reload()
          );
        }
      });
    }
  }
  customElements.define('quantity-input', QuantityInput);

  /* ---------- product form: add to cart ---------- */
  class ProductForm extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('form');
      this.button = this.querySelector('[type="submit"]');
      this.form?.addEventListener('submit', (e) => {
        if (theme.cartType === 'page') return;
        e.preventDefault();
        this.submit();
      });
    }
    submit() {
      const error = this.querySelector('[data-form-error]');
      if (error) error.hidden = true;
      this.button.setAttribute('aria-busy', 'true');
      const data = new FormData(this.form);
      const body = {
        items: [{ id: Number(data.get('id')), quantity: Number(data.get('quantity') || 1) }],
        sections: [cartSection],
      };
      fetchJSON(`${theme.routes.cartAdd}.js`, body)
        .then((res) => {
          renderCart(res.sections);
          document.querySelector('cart-drawer')?.open();
        })
        .catch((err) => alertInline(this, err.message))
        .finally(() => this.button.removeAttribute('aria-busy'));
    }
  }
  customElements.define('product-form', ProductForm);

  /* ---------- money, using the store's own format (Settings → General) ---------- */
  const formatMoney = (cents, format = '₹{{amount_no_decimals}}') => {
    const value = cents / 100;
    const whole = Number.isInteger(value);
    const fmt = (n, decimals) => n.toLocaleString('en-IN', { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
    return format.replace(/\{\{\s*(\w+)\s*\}\}/, (_, key) => {
      switch (key) {
        case 'amount_no_decimals':
        case 'amount_no_decimals_with_comma_separator':
          return fmt(Math.round(value), 0);
        default:
          return fmt(value, whole ? 0 : 2);
      }
    });
  };

  /* ---------- variant picker ---------- */
  class VariantPicker extends HTMLElement {
    connectedCallback() {
      const json = this.querySelector('script[type="application/json"]');
      this.variants = json ? JSON.parse(json.textContent) : [];
      this.section = this.closest('[data-product-section]');
      this.addEventListener('change', () => this.update());
    }
    selectedOptions() {
      return [...this.querySelectorAll('fieldset')].map((fs) => fs.querySelector('input:checked')?.value);
    }
    update() {
      const opts = this.selectedOptions();
      const variant = this.variants.find((v) => v.options.every((o, i) => o === opts[i]));
      const s = this.section;
      if (!s) return;
      const btn = s.querySelector('[data-add-button]');
      const idInput = s.querySelector('input[name="id"]');
      if (!variant) {
        if (btn) { btn.disabled = true; btn.textContent = theme.strings.unavailable; }
        return;
      }
      if (idInput) idInput.value = variant.id;
      const money = (cents) => formatMoney(cents, theme.moneyFormat);
      s.querySelectorAll('[data-price-current]').forEach((el) => (el.textContent = money(variant.price)));
      s.querySelectorAll('[data-price-compare]').forEach((el) => {
        const show = variant.compare_at_price && variant.compare_at_price > variant.price;
        el.hidden = !show;
        if (show) el.textContent = money(variant.compare_at_price);
      });
      s.querySelectorAll('[data-save-badge]').forEach((el) => {
        const show = variant.compare_at_price && variant.compare_at_price > variant.price;
        el.hidden = !show;
        if (show) el.textContent = el.dataset.template.replace('%', Math.floor(((variant.compare_at_price - variant.price) * 100) / variant.compare_at_price));
      });
      s.querySelectorAll('[data-sku]').forEach((el) => (el.textContent = variant.sku || ''));
      s.querySelectorAll('[data-unit-price]').forEach((el) => (el.textContent = variant.unit_label || ''));
      if (btn) {
        btn.disabled = !variant.available;
        btn.textContent = variant.available ? theme.strings.addToCart : theme.strings.soldOut;
      }
      if (variant.featured_media) {
        const gallery = s.querySelector('media-gallery');
        gallery?.show(variant.featured_media.id);
      }
      const url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url);
    }
  }
  customElements.define('variant-picker', VariantPicker);

  /* ---------- media gallery ---------- */
  class MediaGallery extends HTMLElement {
    connectedCallback() {
      this.main = this.querySelector('[data-gallery-main]');
      this.addEventListener('click', (e) => {
        const thumb = e.target.closest('[data-thumb]');
        if (thumb) this.show(thumb.dataset.thumb);
      });
    }
    show(mediaId) {
      const slides = this.querySelectorAll('[data-media-id]');
      slides.forEach((el) => (el.hidden = String(el.dataset.mediaId) !== String(mediaId)));
      this.querySelectorAll('[data-thumb]').forEach((t) =>
        t.setAttribute('aria-current', String(t.dataset.thumb) === String(mediaId))
      );
    }
  }
  customElements.define('media-gallery', MediaGallery);

  /* ---------- mobile menu ---------- */
  document.querySelectorAll('details.mobile-nav').forEach((menu) => {
    menu.addEventListener('toggle', () => {
      const header = menu.closest('.site-header');
      if (header) document.documentElement.style.setProperty('--header-bottom', `${Math.round(header.getBoundingClientRect().bottom)}px`);
      document.documentElement.classList.toggle('nav-open', menu.open);
    });
    menu.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && menu.open) { menu.open = false; menu.querySelector('summary').focus(); }
    });
  });

  /* ---------- header search toggle ---------- */
  document.addEventListener('click', (e) => {
    const toggle = e.target.closest('[data-search-toggle]');
    if (!toggle) return;
    const bar = document.getElementById('HeaderSearch');
    if (!bar) return;
    e.preventDefault();
    bar.hidden = !bar.hidden;
    toggle.setAttribute('aria-expanded', String(!bar.hidden));
    if (!bar.hidden) bar.querySelector('input')?.focus();
  });
})();
